import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { callListTypeLabels, type DialMode } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { usePermissions } from '../../../lib/session';
import { ModuleEmptyPage } from '../../../shell/ModulePages';
import { useTelephonyStatus } from '../../telephony/api';
import { startSession, useOverview, type ListRow } from '../api';
import { dialableContacts, fromLists, orderClause, ratio } from '../format';
import { Crumb, DialerPage, Eyebrow, Fact, InlineLink, LoadError, MetricsBar } from '../ui';
import styles from './start.module.css';

/**
 * Terminierung 01 — Dialer · Einstieg: the metrics of the day, the main queue over all of the setter's
 * active lists („Queue starten · 148", Power/Preview, caller-ID pool, four facts) and the lists as rows whose
 * „Queue starten" appears on hover or keyboard focus and leads to the preview of that list (02/03).
 */
export function DialerStartPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const overview = useOverview();
  const telephony = useTelephonyStatus();
  const [mode, setMode] = useState<DialMode>('power');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runningSession, setRunningSession] = useState<string | null>(null);

  const statisticsLink = permissions.has('controlling.figures') ? (
    <InlineLink to="/dialer/statistics">Meine Statistik</InlineLink>
  ) : null;

  async function start() {
    setPending(true);
    setError(null);
    try {
      const state = await startSession({ dialMode: mode });
      queryClient.setQueryData([...queryRoots.dialer, 'session', 'current'], state);
      if (state.session) void navigate(`/dialer/session/${state.session.id}`);
    } catch (caught) {
      setError(errorMessage(caught));
      if (caught instanceof ApiError) setRunningSession(caught.detailString('sessionId'));
    } finally {
      setPending(false);
    }
  }

  if (overview.isError) {
    return (
      <DialerPage crumb={<Crumb end={statisticsLink}>Dialer</Crumb>}>
        <LoadError
          error={overview.error}
          what="Der Dialer"
          retrying={overview.isFetching}
          onRetry={() => void overview.refetch()}
        />
      </DialerPage>
    );
  }

  const data = overview.data;
  if (data && data.listCount === 0 && data.activeSessionId === null)
    return <ModuleEmptyPage module="dialer" />;

  const activeSession = data?.activeSessionId ?? runningSession;
  const poolLine =
    telephony.data && !telephony.data.configured
      ? telephony.data.message
      : data?.pool
        ? `Rufnummern-Pool ${data.pool.label}`
        : null;

  return (
    <DialerPage crumb={<Crumb end={statisticsLink}>Dialer</Crumb>}>
      {activeSession ? (
        <Banner
          tone="accent"
          actions={
            <Button size="small" onClick={() => void navigate(`/dialer/session/${activeSession}`)}>
              Queue fortsetzen
            </Button>
          }
        >
          Sie haben bereits eine laufende Queue.
        </Banner>
      ) : null}
      <MetricsBar metrics={data?.metrics} loading={overview.isPending} />

      <section className={styles.startCard} aria-labelledby="dialer-start-title">
        <div className={styles.startHead}>
          <div className={styles.startText}>
            <Eyebrow>Alle Ihre Listen zusammen</Eyebrow>
            <h2 id="dialer-start-title" className={styles.startTitle}>
              Queue starten
            </h2>
            {data ? (
              <p className={styles.startLead}>
                {dialableContacts(data.dialable)} {fromLists(data.listCount)}, sortiert nach den Wählregeln:{' '}
                {orderClause(data.orderText, data.followUpsToday)}. Kontakte außerhalb ihres Zeitfensters
                bleiben in der Liste und werden übersprungen.
              </p>
            ) : (
              <div className={styles.startLead}>
                <Skeleton width="80%" height={14} />
                <Skeleton width="60%" height={14} />
              </div>
            )}
          </div>
          <div className={styles.startActions}>
            <button
              type="button"
              className={styles.bigCta}
              disabled={!data || pending || activeSession !== null}
              aria-busy={pending || undefined}
              onClick={() => void start()}
            >
              Queue starten · {data ? data.dialable : '…'}
            </button>
            <div className={styles.modeRow} role="radiogroup" aria-label="Anwahlmodus">
              <span className={styles.modeLabel}>Anwahlmodus</span>
              {(['power', 'preview'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={mode === value}
                  className={clsx(styles.modeChip, mode === value && styles.modeChipOn)}
                  onClick={() => setMode(value)}
                >
                  {value === 'power' ? 'Power' : 'Preview'}
                </button>
              ))}
            </div>
            {poolLine ? <div className={styles.pool}>{poolLine}</div> : null}
          </div>
        </div>
        {error ? (
          <div className={styles.error}>
            <Banner tone="danger">{error}</Banner>
          </div>
        ) : null}
        <div className={styles.facts}>
          <Fact label="Wiedervorlagen heute" value={data ? data.followUpsToday : '–'} />
          <Fact label="Neue Leads seit 8:00" value={data ? data.newLeadsSince8 : '–'} />
          <Fact label="Zeitfenster" value={data ? `${data.window.label}` : '–'} display={false} />
          <Fact label="Ereignistyp" value="je Liste eingestellt" display={false} />
        </div>
      </section>

      <section className={styles.listsCard} aria-labelledby="dialer-lists-title">
        <div className={styles.listsHead}>
          <Eyebrow>
            <span id="dialer-lists-title">Ihre Dialer-Listen</span>
          </Eyebrow>
          <span className={styles.listsNote}>
            Einzeln startbar · Verwaltung unter <InlineLink to="/dialer/lists">Kontakte › Listen</InlineLink>
          </span>
        </div>
        {data
          ? data.lists.map((list) => (
              <ListStartRow
                key={list.id}
                list={list}
                onStart={() => void navigate(`/dialer/preview/${list.id}`)}
              />
            ))
          : Array.from({ length: 3 }, (_, index) => (
              <div key={index} className={styles.row}>
                <div className={styles.rowText}>
                  <Skeleton width={240} height={16} />
                  <Skeleton width={180} height={12} />
                </div>
              </div>
            ))}
      </section>
    </DialerPage>
  );
}

function ListStartRow({ list, onStart }: { list: ListRow; onStart: () => void }) {
  const meta = [
    callListTypeLabels[list.type],
    list.eventType?.name ?? '—',
    `${list.setterCount} Setter`,
  ].join(' · ');
  return (
    <div className={styles.row}>
      <div className={styles.rowText}>
        <div className={styles.rowName}>{list.name}</div>
        <div className={styles.rowMeta}>{meta}</div>
      </div>
      <div className={styles.rowCount}>
        <div className={styles.rowRatio}>{ratio(list.dialable, list.total)}</div>
        <div className={styles.rowRatioLabel}>wählbar / gesamt</div>
      </div>
      <div className={styles.rowStart}>
        <button
          type="button"
          className={styles.rowCta}
          aria-label={`Queue starten: ${list.name}`}
          onClick={onStart}
        >
          Queue starten
        </button>
      </div>
    </div>
  );
}
